import React, { useState, useEffect } from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { TimeRangeType, TimeWindow } from "../TimeSeriesTypes";
import { DataTable } from "../../ui/datatable/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { Log } from "../../../pages/Logs";
import { getLogLeveLIndicatorColor } from "components/SidePanel/Log/utils";
import { DateTimePrecision, displayDateTimeWithSelectedFormat } from "../../../utility/displayDateTime";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import {DefaultToolCallRenderer} from "../ToolRenderers";

// Message interface for the LogsToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Interface that matches the GetLogsHandlerArgs structure
interface GetLogsRequest {
    time_config: {
        type: TimeRangeType;
        time_period?: number;
        time_window?: TimeWindow;
        start_time?: string;
        end_time?: string;
    };
    filters?: { [key: string]: string[] };
    excludeFilters?: { [key: string]: string[] };
    regexes?: string[];
    excludeRegexes?: string[];
    environments?: string[];
}

// Tool renderer for logs data
export function LogsToolRenderer({ message, isToolCall }: ToolRendererProps) {
    const [logs, setLogs] = useState<Log[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // We want to handle the tool call response to show parsed logs in a datatable
    const hasToolCallResponse = !!message.tool_call_response_content;
    const hasToolCallArgs = !!message.tool_call_function_args;

    // Setup columns for DataTable
    const columns: ColumnDef<Log>[] = [
        {
            id: "severity",
            header: "Level",
            cell: ({ row }) => {
                const severity = row.original.severity;
                const indicatorColor = getLogLeveLIndicatorColor(severity);
                return (
                    <div className="flex items-center">
                        <div className={`${indicatorColor} w-2 h-4 mr-2 rounded`}></div>
                        <span>{severity}</span>
                    </div>
                );
            },
        },
        {
            id: "time",
            header: "Time",
            cell: ({ row }) => {
                return (
                    <span>
                        {displayDateTimeWithSelectedFormat(
                            new Date(row.original.time),
                            [DateTimePrecision.Month, DateTimePrecision.Day, DateTimePrecision.Hours, DateTimePrecision.Minutes, DateTimePrecision.Seconds]
                        )}
                    </span>
                );
            },
        },
        {
            id: "serviceName",
            header: "Service",
            cell: ({ row }) => <span>{row.original.serviceName}</span>,
        },
        {
            id: "message",
            header: "Message",
            cell: ({ row }) => {
                const [isOpen, setIsOpen] = useState(false);
                const message = row.original.message;
                const firstLine = message.length > 180 ? message.substring(0, 180) : message;
                const hasMoreContent =  message.length > 180 ;
                
                // If there's only one short line, just display it directly
                if (!hasMoreContent) {
                    return <span className="text-textmedium max-w-[600px] inline-block">{firstLine}</span>;
                }
                
                return (
                    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full max-w-full">
                        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-textmedium hover:text-textlight transition-colors w-full text-left">
                            {isOpen ? <ChevronDown className="h-4 w-4 flex-shrink-0"/> : <ChevronRight className="h-4 w-4 flex-shrink-0"/>}
                            <span className="truncate max-w-[600px]">{firstLine}</span>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="max-w-full">
                            <div className="mt-2 bg-[#1D2739] rounded-lg p-4 text-sm font-mono text-textmedium overflow-auto max-h-96" style={{ width: '800px', maxWidth: '100%' }}>
                                <pre className="whitespace-pre-wrap break-words">{message}</pre>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                );
            },
            size: 800, // Set a specific size for the message column
        },
    ];

    // Parse logs from the tool call response when available
    useEffect(() => {
        if (hasToolCallResponse) {
            setIsLoading(true);
            try {
                const responseData = JSON.parse(message.tool_call_response_content || '{}');
                
                // Check if the response contains logs
                if (responseData && Array.isArray(responseData.logs)) {
                    // Transform logs to match the Log interface
                    const parsedLogs: Log[] = responseData.logs.map((log: any) => {
                        // Convert string maps to actual Map objects if needed
                        let logAttributes = new Map<string, string>();
                        let resourceAttributes = new Map<string, string>();
                        
                        if (log.logAttributes) {
                            // Handle if logAttributes is an object or already a Map
                            if (typeof log.logAttributes === 'object' && !Array.isArray(log.logAttributes)) {
                                Object.entries(log.logAttributes).forEach(([key, value]) => {
                                    logAttributes.set(key, value as string);
                                });
                            }
                        }
                        
                        if (log.resourceAttributes) {
                            // Handle if resourceAttributes is an object or already a Map
                            if (typeof log.resourceAttributes === 'object' && !Array.isArray(log.resourceAttributes)) {
                                Object.entries(log.resourceAttributes).forEach(([key, value]) => {
                                    resourceAttributes.set(key, value as string);
                                });
                            }
                        }
                        
                        return {
                            time: log.time,
                            severity: log.severity || 'unknown',
                            message: log.message || '',
                            serviceName: log.serviceName || '',
                            logAttributes,
                            resourceAttributes
                        };
                    });
                    
                    setLogs(parsedLogs);
                }
            } catch (error) {
                console.error('Error parsing logs from response:', error);
            } finally {
                setIsLoading(false);
            }
        }
    }, [message.tool_call_response_content, hasToolCallResponse]);

    // If this is just the tool call with args but no response yet, render the original component
    if (!hasToolCallResponse && hasToolCallArgs) {
        let logsRequest: GetLogsRequest | null = null;
        try {
            logsRequest = JSON.parse(message.tool_call_function_args || '{}');
        } catch (error) {
            console.error('Error parsing logs request data:', error);
            return null;
        }

        // Validate request
        if (!logsRequest || !logsRequest.time_config) {
            return null;
        }

        return (
            <div className="w-full">
                <DefaultToolCallRenderer message={message}/>
            </div>
        );
    }

    // If we have a tool call response, render the logs in a datatable
    if (hasToolCallResponse) {
        return (
            <div className="w-full">
                <MarkdownRenderer content={message.content} />
                <div className="border  rounded overflow-hidden">
                    <div className="max-h-[600px] overflow-auto">
                        <DataTable
                            columns={columns}
                            data={logs}
                            tableTitle="Logs"
                            filters={[]}
                            isLoading={isLoading}
                            showPagination={true}
                            removeBorder={true}
                            emptyStateMessage={<div className="p-4 text-center">No logs found</div>}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return null;
}

// Static method to determine if this renderer should render a message
LogsToolRenderer.shouldRender = (message: Message): boolean => {
    // Only render if it's a tool call or a tool response for get_logs
    if (message.tool_call_function_name === 'get_logs') {
        return true;
    }
    if (message.tool_call_response_id && message.tool_name === 'get_logs') {
        return true;
    }
    return false;
}; 